import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { ErrorNotification } from '../common/ErrorNotification';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Main layout for authenticated pages
 * Includes header, sidebar, and renders child routes via Outlet
 */
const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger',
  });
  const [errorNotification, setErrorNotification] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Check if current page is chat
  const isChatPage = location.pathname === '/chat';

  const handleMenuToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleCloseSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleLogout = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      variant: 'primary',
      onConfirm: async () => {
        try {
          await logout();
          navigate('/login');
        } catch (error) {
          console.error('Error logging out:', error);
          setErrorNotification('Failed to logout. Please try again.');
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      variant: 'danger',
    });
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Header user={user} onLogout={handleLogout} onMenuToggle={handleMenuToggle} />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar isOpen={isMobileSidebarOpen} onClose={handleCloseSidebar} />

        <main className={`flex-1 ${isChatPage ? 'p-4 overflow-hidden' : 'py-8 px-4 sm:px-6 lg:px-8 overflow-auto'}`}>
          <Outlet />
        </main>
      </div>

      {/* Global Dialogs */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />

      <ErrorNotification
        message={errorNotification}
        onClose={() => setErrorNotification(null)}
      />
    </div>
  );
};

export default MainLayout;
